/**
 * Customer asset library (QM-A08-001) via /v1/deliverables/assets, with "Import from Google Drive / OneDrive"
 * (DriveImport, behind the assets.cloud_import flag) and "Upload files" (AssetUpload, direct R2 uploads behind the
 * assets.uploads flag). Imported assets show their source.
 */
import { Link, useSearchParams } from 'react-router';
import { Badge, PageHeader } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { ScreenBody, str, useWorkspaceName } from '../_shared/ListScreen';
import type { UploadUsageView } from '@qm/contracts/asset-uploads';
import { AssetUpload, DeleteAssetButton } from './AssetUpload';
import { DriveImport } from './DriveImport';

const SOURCES = ['upload', 'google_drive', 'onedrive', 'production'];
const KINDS = ['image', 'video', 'document'];
const STATUSES = ['awaiting_upload', 'scanning', 'available', 'rejected'];

export function AssetsPage() {
  const { t } = useApp();
  const f = useFormat();
  const [params] = useSearchParams();
  const assets = useResource<unknown>('/v1/deliverables/assets');
  // uploads flag (assets.uploads): /usage answers only while it is on; then each row gets Delete
  const uploads = useResource<UploadUsageView>('/v1/deliverables/assets/usage');
  const workspace = useWorkspaceName();
  const pick = (list: string[], v: unknown, prefix: string) => (list.includes(str(v)) ? t(`${prefix}.${str(v)}`) : str(v));
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t('deliverables.assets.title')} intro={t('deliverables.assets.intro')}
        actions={<Link to="/content">{t('deliverables.assets.back')}</Link>} />
      <AssetUpload onUploaded={assets.reload} />
      <DriveImport connected={params.get('drive')} onImported={assets.reload} />
      <ScreenBody res={assets} caption={t('deliverables.assets.title')} emptyKey="deliverables.assets.empty"
        columns={[
          { key: 'title', header: t('shell.col.title'), render: (r) => <bdi>{f.text(r.title)}</bdi> },
          { key: 'kind', header: t('shell.col.type'), render: (r) => pick(KINDS, r.kind, 'deliverables.assets.kind') },
          { key: 'source', header: t('deliverables.assets.col.source'), render: (r) => pick(SOURCES, r.source, 'deliverables.assets.source') },
          { key: 'status', header: t('shell.col.status'), render: (r) => <Badge tone={r.status === 'rejected' ? 'error' : 'neutral'}>{pick(STATUSES, r.status, 'deliverables.assets.status')}</Badge> },
          { key: 'updatedAt', header: t('deliverables.assets.col.updated'), render: (r) => f.date(str(r.updatedAt)) },
          ...(uploads.status === 'ready' ? [{
            key: 'actions', header: t('deliverables.assets.col.actions'),
            render: (r: Record<string, unknown>) => <DeleteAssetButton id={str(r.id)} title={str(r.title)} onDeleted={() => { assets.reload(); uploads.reload(); }} />,
          }] : []),
        ]} />
    </section>
  );
}
